import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSellerAnalytics } from '../lib/analyticsService';
import { COLORS } from '../lib/styleGuide';
import { 
  ArrowUp, 
  Show, 
  ShoppingCart, 
  TickSquare, 
  Calendar,
  ArrowDown,
  Search
} from 'react-iconly';

// Typography constants
const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 400
};

const buttonFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 600
};

export function SellerConversions() {
  const { currentUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [sortBy, setSortBy] = useState('views');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch analytics data
  useEffect(() => {
    if (currentUser?.uid) {
      fetchAnalytics();
    }
  }, [currentUser?.uid, dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await getSellerAnalytics(currentUser.uid, dateRange);
      
      if (result.success) {
        setAnalytics(result.data);
        // Show message if no store found
        if (result.message) {
          setError(result.message);
        }
      } else {
        setError(result.error || 'Failed to fetch analytics data');
      }
    } catch (err) {
      setError('An error occurred while fetching analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Sort products based on current sort settings
  const getSortedProducts = () => {
    if (!analytics?.products) return [];
    
    let sorted = [...analytics.products];
    
    sorted.sort((a, b) => {
      let aValue = a[sortBy] || 0;
      let bValue = b[sortBy] || 0;
      
      if (sortBy.includes('Conversion')) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return sorted;
  };

  // Filter products by search term
  const getFilteredProducts = () => {
    const sorted = getSortedProducts();
    
    if (!searchTerm) return sorted;
    
    return sorted.filter(product => 
      product.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.productId?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString();
  };

  // Get date range label
  const getDateRangeLabel = (range) => {
    switch (range) {
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      case 'all': return 'All Time';
      default: return 'Last 30 Days';
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    
    return sortOrder === 'asc' ? 
      <ArrowUp set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} /> :
      <ArrowDown set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} />;
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        padding: '20px'
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          padding: '20px'
        }}>
          <div style={{ 
            background: COLORS.NEUTRAL_WHITE, 
            borderRadius: 16, 
            padding: 40,
            textAlign: 'center',
            color: COLORS.ACCENT_GRAY
          }}>
            <div style={{ fontSize: 18, ...bodyFont }}>Loading conversion data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !analytics) {
    const isNoStoreError = error.includes('No store found');
    
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        padding: '20px'
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          padding: '20px'
        }}>
          <div style={{ 
            background: COLORS.NEUTRAL_WHITE, 
            borderRadius: 16, 
            padding: 40,
            textAlign: 'center',
            color: isNoStoreError ? COLORS.ACCENT_GRAY : COLORS.ERROR_RED
          }}>
            <div style={{ fontSize: 18, ...bodyFont, marginBottom: 16 }}>
              {isNoStoreError ? 'No Store Found' : 'Error loading data'}
            </div>
            <div style={{ fontSize: 14, ...bodyFont, color: COLORS.ACCENT_GRAY, marginBottom: 20 }}>
              {isNoStoreError 
                ? 'You need to create a store before you can view conversion analytics. Set up your store to start tracking customer interactions and sales performance.'
                : error
              }
            </div>
            {isNoStoreError ? (
              <button 
                onClick={() => window.location.href = '/seller-hub'}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: COLORS.NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...buttonFont
                }}
              >
                Go to Seller Hub
              </button>
            ) : (
              <button 
                onClick={fetchAnalytics}
                style={{
                  background: COLORS.PRIMARY_GRADIENT,
                  color: COLORS.NEUTRAL_WHITE,
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 24px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...buttonFont
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: COLORS.NEUTRAL_BG,
      padding: '20px'
    }}>
      <div style={{ 
        maxWidth: 1200, 
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ 
          background: COLORS.NEUTRAL_WHITE, 
          borderRadius: 16, 
          padding: 24,
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ 
              ...headingFont, 
              fontSize: 24, 
              color: COLORS.NEUTRAL_TEXT, 
              margin: '0 0 8px 0'
            }}>
              Conversion Analytics
            </h1>
            <p style={{ 
              ...bodyFont, 
              fontSize: 14, 
              color: COLORS.ACCENT_GRAY, 
              margin: 0
            }}>
              Track your store's performance and product conversion rates
            </p>
          </div>
          
          {/* Date Range Filter */}
          <div style={{ display: 'flex', gap: 8 }}>
            {['7d', '30d', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                style={{
                  background: dateRange === range ? COLORS.PRIMARY_GRADIENT : COLORS.NEUTRAL_WHITE,
                  color: dateRange === range ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                  border: `2px solid ${dateRange === range ? 'transparent' : COLORS.NEUTRAL_GRAY}`,
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ...buttonFont
                }}
              >
                {getDateRangeLabel(range)}
              </button>
            ))}
          </div>
        </div>

        {/* Store Level Metrics */}
        {analytics?.store && (
          <div style={{ 
            background: COLORS.NEUTRAL_WHITE, 
            borderRadius: 16, 
            padding: 24,
            marginBottom: 24
          }}>
            <h2 style={{ 
              ...headingFont, 
              fontSize: 18, 
              color: COLORS.NEUTRAL_TEXT, 
              margin: '0 0 20px 0'
            }}>
              Store Performance Overview
            </h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 20
            }}>
              {/* Store Views */}
              <div style={{ 
                background: COLORS.NEUTRAL_BG, 
                borderRadius: 12, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ 
                  background: COLORS.PRIMARY_GRADIENT, 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 12px auto'
                }}>
                  <Show set="bold" size={24} primaryColor={COLORS.NEUTRAL_WHITE} />
                </div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: 4
                }}>
                  {formatNumber(analytics.store.views)}
                </div>
                <div style={{ 
                  fontSize: 12, 
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Store Views
                </div>
              </div>

              {/* Total Orders */}
              <div style={{ 
                background: COLORS.NEUTRAL_BG, 
                borderRadius: 12, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ 
                  background: COLORS.PRIMARY_GRADIENT, 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 12px auto'
                }}>
                  <TickSquare set="bold" size={24} primaryColor={COLORS.NEUTRAL_WHITE} />
                </div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: 4
                }}>
                  {formatNumber(analytics.store.totalOrders)}
                </div>
                <div style={{ 
                  fontSize: 12, 
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Total Orders
                </div>
              </div>

              {/* Store Conversion Rate */}
              <div style={{ 
                background: COLORS.NEUTRAL_BG, 
                borderRadius: 12, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ 
                  background: COLORS.PRIMARY_GRADIENT, 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 12px auto'
                }}>
                  <ArrowUp set="bold" size={24} primaryColor={COLORS.NEUTRAL_WHITE} />
                </div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: 4
                }}>
                  {formatPercentage(analytics.store.conversionRate)}
                </div>
                <div style={{ 
                  fontSize: 12, 
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Store Conversion Rate
                </div>
              </div>

              {/* Unique Sessions */}
              <div style={{ 
                background: COLORS.NEUTRAL_BG, 
                borderRadius: 12, 
                padding: 20,
                textAlign: 'center'
              }}>
                <div style={{ 
                  background: COLORS.PRIMARY_GRADIENT, 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  margin: '0 auto 12px auto'
                }}>
                  <Calendar set="bold" size={24} primaryColor={COLORS.NEUTRAL_WHITE} />
                </div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 700, 
                  color: COLORS.NEUTRAL_TEXT,
                  marginBottom: 4
                }}>
                  {formatNumber(analytics.uniqueSessions || 0)}
                </div>
                <div style={{ 
                  fontSize: 12, 
                  color: COLORS.ACCENT_GRAY,
                  ...bodyFont
                }}>
                  Unique Sessions
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Level Metrics */}
        <div style={{ 
          background: COLORS.NEUTRAL_WHITE, 
          borderRadius: 16, 
          padding: 24
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 20
          }}>
            <h2 style={{ 
              ...headingFont, 
              fontSize: 18, 
              color: COLORS.NEUTRAL_TEXT, 
              margin: 0
            }}>
              Product Performance
            </h2>
            
            {/* Search */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: COLORS.NEUTRAL_BG, 
              borderRadius: 8, 
              padding: '8px 12px',
              width: 300
            }}>
              <Search set="bold" size={16} primaryColor={COLORS.ACCENT_GRAY} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  marginLeft: 8,
                  fontSize: 14,
                  outline: 'none',
                  width: '100%',
                  ...bodyFont
                }}
              />
            </div>
          </div>

          {filteredProducts.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 40, 
              color: COLORS.ACCENT_GRAY,
              ...bodyFont
            }}>
              {searchTerm ? 'No products match your search' : 'No product data available for this period'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: 14,
                ...bodyFont
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: `2px solid ${COLORS.NEUTRAL_GRAY}`,
                    background: COLORS.NEUTRAL_BG
                  }}>
                    <th style={{ 
                      padding: '16px 12px', 
                      textAlign: 'left', 
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT
                    }}>
                      Product
                    </th>
                    <th style={{ 
                      padding: '16px 12px', 
                      textAlign: 'center', 
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT,
                      cursor: 'pointer'
                    }} onClick={() => handleSort('views')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        Views {getSortIcon('views')}
                      </div>
                    </th>
                    <th style={{ 
                      padding: '16px 12px', 
                      textAlign: 'center', 
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT,
                      cursor: 'pointer'
                    }} onClick={() => handleSort('addToCarts')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        Add to Cart {getSortIcon('addToCarts')}
                      </div>
                    </th>
                    <th style={{ 
                      padding: '16px 12px', 
                      textAlign: 'center', 
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT,
                      cursor: 'pointer'
                    }} onClick={() => handleSort('orders')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        Orders {getSortIcon('orders')}
                      </div>
                    </th>
                    <th style={{ 
                      padding: '16px 12px', 
                      textAlign: 'center', 
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT,
                      cursor: 'pointer'
                    }} onClick={() => handleSort('productConversionRate')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        Product Conv. % {getSortIcon('productConversionRate')}
                      </div>
                    </th>
                    <th style={{ 
                      padding: '16px 12px', 
                      textAlign: 'center', 
                      fontWeight: 600,
                      color: COLORS.NEUTRAL_TEXT,
                      cursor: 'pointer'
                    }} onClick={() => handleSort('cartConversionRate')}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        Cart Conv. % {getSortIcon('cartConversionRate')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, index) => (
                    <tr key={product.productId} style={{ 
                      borderBottom: `1px solid ${COLORS.NEUTRAL_GRAY}`,
                      background: index % 2 === 0 ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_BG
                    }}>
                      <td style={{ 
                        padding: '16px 12px', 
                        fontWeight: 600,
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        {product.productName || `Product ${product.productId.slice(-6)}`}
                      </td>
                      <td style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center',
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        {formatNumber(product.views)}
                      </td>
                      <td style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center',
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        {formatNumber(product.addToCarts)}
                      </td>
                      <td style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center',
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        {formatNumber(product.orders)}
                      </td>
                      <td style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center',
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        {formatPercentage(product.productConversionRate)}
                      </td>
                      <td style={{ 
                        padding: '16px 12px', 
                        textAlign: 'center',
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        {formatPercentage(product.cartConversionRate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
